import { capitalizeFirstLetter } from '@eneco/common/string';
import {
  DC_Domain_Models_Products_CostDetailType,
  DC_Domain_Models_Products_ProductType,
  Products_Offers_ProductCostDetail,
  Products_Offers_V3_OfferProductCombinationResponseModel,
  Products_Offers_V3_OfferProductResponseModel,
  Products_Offers_V3_OfferResponseModel,
  Products_Offers_V3_ProductMpcResponseModel,
  Products_ProductRates_ProductRateDetailsViewModel,
  Products_VatModel,
} from '@monorepo-types/dc';

import { FlowContextSme } from '../context';
import {
  CostDetails,
  EnergyType,
  FutureRedeliveryFields,
  Offer,
  OfferContractSpan,
  OfferTermsAndConditions,
  Price,
  Product,
} from '../types';

const getOfferCostDetailsDetails = (products: Product[] = []): Record<string, unknown> =>
  products.reduce((acc, product) => Object.assign(acc, { ...product.costDetails }), {});

export const getUpsellProductAvailability = (
  productType: DC_Domain_Models_Products_ProductType,
  flowContext: FlowContextSme,
  offer?: Products_Offers_V3_OfferResponseModel,
): boolean => getUpsellProductPrice(productType, flowContext, offer) !== 0;

const getOfferContractSpanFromProducts = (products: Product[]): OfferContractSpan[] =>
  products.map(product => {
    return product.contractSpan
      ? {
          ...product.contractSpan,
          // Threat "warmthEkv" and "bronwarmte" as regular "warmth" product
          type: product.type === 'warmthEkv' || product.type === 'bronwarmte' ? 'warmth' : product.type,
        }
      : {
          duration: 0,
          byPeriod: 'D',
          type: 'unknown',
        };
  });

const selectCostDetailsByType = (
  costDetailsType: DC_Domain_Models_Products_CostDetailType,
  costDetails?: Products_Offers_ProductCostDetail[] | null,
): Products_VatModel | undefined => (costDetails ?? []).find(i => i.type === costDetailsType)?.costs;

/**
 * Picks the product combination for a given `offerType` (`discountType`, e.g. `newEnergy1Year`).
 * A single duration can expose two combinations \u2014 with and without a bundled cashback product \u2014
 * so among the matches the cashback variant is preferred, mirroring the legacy V1 selection behaviour.
 */
export const selectOfferProductCombination = (
  productCombinations: Products_Offers_V3_OfferProductCombinationResponseModel[],
  selectedProductTypes: DC_Domain_Models_Products_ProductType[],
  offerType?: string,
  hasUpsellGas = false,
  hasUpsellElectricity = false,
): Products_Offers_V3_OfferProductCombinationResponseModel | undefined => {
  const matchesUpsellState = (product: Products_Offers_V3_ProductMpcResponseModel): boolean => {
    if (product.productType === 'electricity') {
      if (hasUpsellElectricity && !!product.upsellMarketingProductCode) return false;
      if (!hasUpsellElectricity && !!product.downsellMarketingProductCode) return false;
    }

    if (product.productType === 'gas') {
      if (hasUpsellGas && !!product.upsellMarketingProductCode) return false;
      if (!hasUpsellGas && !!product.downsellMarketingProductCode) return false;
    }

    return true;
  };

  const candidates = productCombinations.filter(
    combination =>
      combination.discountType === offerType &&
      combination.products?.every(
        product => selectedProductTypes.includes(product.productType) && matchesUpsellState(product),
      ),
  );

  // No fallback to a non-matching combination: that would silently swap in products the customer never selected.
  return (
    candidates.find(combination => combination.products?.some(product => product.productType === 'cashback')) ??
    candidates[0]
  );
};

export const getEnergyTypesFromProductTypes = (
  productCombinations: Array<Array<DC_Domain_Models_Products_ProductType>>,
): EnergyType[] => {
  const energyTypes: EnergyType[] = [];

  productCombinations?.forEach(productCombination => {
    // Threat "warmthEkv" as regular "warmth" product
    const warmthEkvIndex = productCombination.indexOf('warmthEkv');

    if (warmthEkvIndex !== -1) {
      productCombination[warmthEkvIndex] = 'warmth';
    }

    // Remove "warmtewisselaar" product since it is not an "energy" type product
    const warmtewisselaarIndex = productCombination.indexOf('warmtewisselaar');
    if (warmtewisselaarIndex !== -1) {
      productCombination.splice(warmtewisselaarIndex, 1);
    }

    // Product Combination Check: ELECTRICITY && GAS && SOMETHING
    if (productCombination.includes(EnergyType.Electricity) && productCombination.includes(EnergyType.Gas)) {
      // ELECTRICITY && GAS && WARMTH && TAPWATER
      if (productCombination.includes(EnergyType.Warmth) && productCombination.includes(EnergyType.TapWater)) {
        energyTypes.unshift(EnergyType.ElectricityAndGasAndWarmthAndTapWater);
      }
      // ELECTRICITY && GAS && WARMTH
      else if (productCombination.includes(EnergyType.Warmth)) {
        energyTypes.unshift(EnergyType.ElectricityAndGasAndWarmth);
      }
      // ELECTRICITY && GAS
      else {
        energyTypes.unshift(EnergyType.ElectricityAndGas);
      }
    }
    // Product Combination Check: ELECTRICITY && WARMTH && SOMETHING
    else if (productCombination.includes(EnergyType.Electricity) && productCombination.includes(EnergyType.Warmth)) {
      // ELECTRICITY && WARMTH && TAPWATER
      if (productCombination.includes(EnergyType.TapWater)) {
        energyTypes.unshift(EnergyType.ElectricityAndWarmthAndTapWater);
        // ELECTRICITY && WARMTH
      } else {
        energyTypes.unshift(EnergyType.ElectricityAndWarmth);
      }
    }
    // Procuct Combination Check: WARMTH && TAPWATER
    else if (productCombination.includes(EnergyType.Warmth) && productCombination.includes(EnergyType.TapWater)) {
      // WARMTH && TAPWATER
      energyTypes.unshift(EnergyType.WarmthAndTapWater);
    }
    // Product Combination Check: ELECTRICITY && GEOTHERMALHEATING (bronwarmte)
    else if (productCombination.includes(EnergyType.Electricity) && productCombination.includes('bronwarmte')) {
      energyTypes.unshift(EnergyType.ElectricityAndGeothermalHeating);
    }
    // Single Product Choice Check:
    else if (productCombination.length === 1) {
      const capitalizedProductType = capitalizeFirstLetter(productCombination[0]);

      if (productCombination.includes('bronwarmte')) {
        energyTypes.unshift(EnergyType.GeothermalHeating);
      } else if (capitalizedProductType in EnergyType) {
        energyTypes.unshift(EnergyType[capitalizedProductType as keyof typeof EnergyType]);
      }
    }
  });

  return energyTypes;
};

export const getProductTypesFromEnergyType = (
  energyType?: EnergyType,
): Array<DC_Domain_Models_Products_ProductType> => {
  switch (energyType) {
    case EnergyType.Electricity:
      return ['electricity'];
    case EnergyType.ElectricityAndGas:
      return ['electricity', 'gas'];
    case EnergyType.ElectricityAndGasAndWarmth:
      return ['electricity', 'gas', 'warmth', 'warmthEkv', 'warmtewisselaar'];
    case EnergyType.ElectricityAndGasAndWarmthAndTapWater:
      return ['electricity', 'gas', 'warmth', 'warmthEkv', 'tapwater', 'warmtewisselaar'];
    case EnergyType.ElectricityAndWarmth:
      return ['electricity', 'warmth', 'warmthEkv', 'warmtewisselaar'];
    case EnergyType.ElectricityAndWarmthAndTapWater:
      return ['electricity', 'warmth', 'warmthEkv', 'tapwater', 'warmtewisselaar'];
    case EnergyType.ElectricityAndGeothermalHeating:
      return ['electricity', 'bronwarmte', 'warmth'];
    case EnergyType.Gas:
      return ['gas'];
    case EnergyType.Warmth:
      return ['warmth', 'warmthEkv', 'warmtewisselaar'];
    case EnergyType.WarmthAndTapWater:
      return ['warmth', 'warmthEkv', 'tapwater', 'warmtewisselaar'];
    case EnergyType.GeothermalHeating:
      return ['bronwarmte', 'warmth'];
    default:
      return [];
  }
};

export const getProductCategoryFromProductType = (
  productType: DC_Domain_Models_Products_ProductType,
): 'commodity' | 'non-commodity' => {
  if (
    productType === 'gas' ||
    productType === 'electricity' ||
    productType === 'warmth' ||
    productType === 'warmthEkv' ||
    productType === 'tapwater'
  ) {
    return 'commodity';
  }

  return 'non-commodity';
};

/** Looks up the full product entry (rates, cost details, description) for a combination's product line. */
const findOfferProduct = (
  offer: Products_Offers_V3_OfferResponseModel,
  marketingProductCode?: string | null,
): Products_Offers_V3_OfferProductResponseModel | undefined =>
  offer.products?.find(p => p.marketingProductCode === marketingProductCode);

// Keeps the volumes DC billed a cost detail over next to its costs, so a price breakdown can multiply its rate
// by the same volume the amount was calculated on (the consumer mapper does the same).
const getCostDetailVolumes = ({ usage, netUsage }: Products_Offers_ProductCostDetail) => ({
  ...(typeof usage === 'number' && { usage }),
  ...(typeof netUsage === 'number' && { netUsage }),
});

const getFutureRedeliveryFields = (product: Products_Offers_V3_OfferProductResponseModel): FutureRedeliveryFields => ({
  productFutureRate: product.productFutureRate,
  futureCostDetails: product.futureCostDetails ?? undefined,
  estimatedUsages: product.estimatedUsages,
});

// Netting only concerns electricity, so the offer-level future fields come from the electricity product of
// the selected combination — the same source the consumer mapper uses.
const getOfferFutureRedeliveryFields = (products: Product[]): FutureRedeliveryFields => {
  const { productFutureRate, futureCostDetails, estimatedUsages } =
    products.find(product => product.type === 'electricity') ?? {};

  return { productFutureRate, futureCostDetails, estimatedUsages };
};

// The price breakdown always displays yearly figures, but V3 reports some fixed-charge rate details
// (e.g. `standingcharge`) per month instead of per year for SME/MKB offers - annualize those so the
// numbers stay comparable to the rest of the breakdown.
const toYearlyRate = (item: Products_ProductRates_ProductRateDetailsViewModel) => {
  const multiplier = item.byPeriod === 'M' ? 12 : 1;

  return {
    vat: item.vat * multiplier,
    vatExcluded: item.vatExcluded * multiplier,
    vatIncluded: item.vatIncluded * multiplier,
  };
};

const getProducts = (
  selectedProductCombination: Products_Offers_V3_OfferProductCombinationResponseModel,
  offer: Products_Offers_V3_OfferResponseModel,
): Product[] =>
  (selectedProductCombination.products ?? [])
    .filter(p => p.productType !== 'cashback')
    .reduce<Product[]>((products, combinationProduct) => {
      const selectedProduct = findOfferProduct(offer, combinationProduct.marketingProductCode);

      if (!selectedProduct) {
        return products;
      }

      products.push({
        type: selectedProduct.productType,

        code: selectedProduct.marketingProductCode ?? '',
        productCombinationCode: selectedProductCombination.discountCode ?? undefined,

        variant: selectedProductCombination.discountType,

        title: selectedProduct.description ?? undefined,

        contractSpan: selectedProduct.contractSpan,

        costsMonthly: selectCostDetailsByType('monthlyCosts', selectedProduct.costDetails),
        costsYearly: selectCostDetailsByType('yearlyCosts', selectedProduct.costDetails),

        costDetails: {
          [`${combinationProduct.productType}CostDetails`]: selectedProduct.productRateDetails?.reduce(
            (acc, item) =>
              Object.assign(acc, {
                [item.type]: {
                  price: toYearlyRate(item),
                  components: item.components,
                },
              }),
            {},
          ),

          [`${combinationProduct.productType}EnergyTaxDetails`]: selectedProduct.energyTaxDetails?.tariffs?.reduce(
            (acc, item) =>
              Object.assign(acc, {
                [`bracketNr${item.bracketNr}`]: {
                  active: item.usage > 0,
                  usage: item.usage,
                  usageMax: item.bracketUsageMax,
                  usageMin: item.bracketUsageMin,
                  price: {
                    vatAmount: item.vatAmount,
                    vatExcluded: item.vatExcluded,
                    vatIncluded: item.vatIncluded,
                  },
                },
              }),
            {},
          ),

          [`${combinationProduct.productType}Total`]: selectedProduct.costDetails?.reduce(
            (acc, item) => Object.assign(acc, { [item.type ?? '']: { ...item.costs, ...getCostDetailVolumes(item) } }),
            {},
          ),
        },

        contractStartDate: offer.contractStartDate ?? undefined,
        contractEndDate: selectedProduct.contractEndDate ?? undefined,

        ...getFutureRedeliveryFields(selectedProduct),
      });

      return products;
    }, []);

const getCashbackProductDetails = (
  cashbackMpc: Products_Offers_V3_ProductMpcResponseModel,
  offer: Products_Offers_V3_OfferResponseModel,
): Products_Offers_V3_OfferProductResponseModel | undefined =>
  offer.products?.find(
    p =>
      p.marketingProductCode === cashbackMpc.marketingProductCode ||
      p.internalDcMarketingProductCode === cashbackMpc.marketingProductCode,
  );

const getCashbackValue = (
  selectedProductCombination: Products_Offers_V3_OfferProductCombinationResponseModel,
  offer: Products_Offers_V3_OfferResponseModel,
): Price | undefined => {
  const cashbackMpc = selectedProductCombination.products?.find(p => p.productType === 'cashback');
  const cashbackProduct = cashbackMpc && getCashbackProductDetails(cashbackMpc, offer);
  const costDetails = cashbackProduct?.costDetails?.find(
    c => c.type === 'cashBackDirect' || c.type === 'cashBackOnYearNote',
  );

  return (
    costDetails && {
      // V3 carries the cashback amount under `costs.vatIncluded` instead of the legacy `.value` string.
      value: Number(costDetails.value || costDetails.costs?.vatIncluded || 0),
      type: costDetails.type ?? 'unKnown',
    }
  );
};

/**
 * Reads the price delta for the given product's own upsell/downsell sibling directly off the
 * currently selected combination \u2014 no need to re-select a whole different combination for this,
 * the sibling's marketing product code is already on the product line.
 */
export const getUpsellProductPrice = (
  productType: DC_Domain_Models_Products_ProductType,
  flowContext: FlowContextSme,
  offer?: Products_Offers_V3_OfferResponseModel,
): number => {
  const { offerType, energyType, hasUpsellGas = false, hasUpsellElectricity = false, isSME } = flowContext;

  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';

  if (!offer?.productCombinations || !offerType || !energyType) {
    return 0;
  }

  const selectedProductTypes = [...getProductTypesFromEnergyType(energyType), 'cashback' as const];
  const selectedProductCombination = selectOfferProductCombination(
    offer.productCombinations,
    selectedProductTypes,
    offerType,
    hasUpsellGas,
    hasUpsellElectricity,
  );
  const selectedProduct = selectedProductCombination?.products?.find(p => p.productType === productType);

  const selectedProductCostDetails = findOfferProduct(offer, selectedProduct?.marketingProductCode)?.costDetails;
  const alternativeProductCostDetails = offer.products?.find(
    p =>
      p.marketingProductCode === selectedProduct?.upsellMarketingProductCode ||
      p.marketingProductCode === selectedProduct?.downsellMarketingProductCode,
  )?.costDetails;

  const selectedProductPrice = selectCostDetailsByType('monthlyCosts', selectedProductCostDetails)?.[costsType] ?? 0;
  const alternativeProductPrice =
    selectCostDetailsByType('monthlyCosts', alternativeProductCostDetails)?.[costsType] ?? selectedProductPrice;

  return Math.abs(selectedProductPrice - alternativeProductPrice);
};

export const getOffer = (
  flowContext: FlowContextSme,
  offer?: Products_Offers_V3_OfferResponseModel,
): Offer | undefined => {
  const { offerType, energyType, hasUpsellGas = false, hasUpsellElectricity = false } = flowContext;

  if (offer?.productCombinations && offerType && energyType) {
    const selectedProductTypes = [...getProductTypesFromEnergyType(energyType), 'cashback' as const];
    const selectedProductCombination = selectOfferProductCombination(
      offer.productCombinations,
      selectedProductTypes,
      offerType,
      hasUpsellGas,
      hasUpsellElectricity,
    );

    if (!selectedProductCombination) {
      return undefined;
    }

    const selectedProducts = getProducts(selectedProductCombination, offer);

    const selectedProductCombinationMonthlyCosts = selectCostDetailsByType(
      'monthlyCosts',
      selectedProductCombination?.costDetails,
    );
    const selectedProductCombinationYearlyCosts = selectCostDetailsByType(
      'yearlyCosts',
      selectedProductCombination?.costDetails,
    );
    const selectedProductCombinationYearlyPromotionCosts = selectCostDetailsByType(
      'yearlyCostsPromotion',
      selectedProductCombination?.costDetails,
    );

    const selectedCashbackMpc = selectedProductCombination.products?.find(p => p.productType === 'cashback');
    const cashbackProductDetails = selectedCashbackMpc && getCashbackProductDetails(selectedCashbackMpc, offer);
    const selectedCashbackProductCosts = selectedCashbackMpc && getCashbackValue(selectedProductCombination, offer);

    return {
      type: selectedProductCombination.discountType ?? offerType,

      products: selectedProducts,

      // V3 dropped the `offers[]` / `mostPopularChoice` grouping, so nothing highlights a default choice anymore.
      mostPopularChoice: false,

      costsMonthly: selectedProductCombinationMonthlyCosts,
      costsYearly: selectedProductCombinationYearlyCosts,

      ...(selectedProductCombinationYearlyCosts?.vatExcluded !==
        selectedProductCombinationYearlyPromotionCosts?.vatExcluded && {
        costsYearlyPromotion: selectedProductCombinationYearlyPromotionCosts,
      }),

      costDetails: {
        ...getOfferCostDetailsDetails(selectedProducts),

        ...(selectedCashbackProductCosts && {
          cashback: {
            value: selectedCashbackProductCosts.value ?? 0,
            type: selectedCashbackProductCosts.type,
          },
        }),
      },
      costDiscounts: {
        ...(selectedCashbackProductCosts && {
          productDetails: cashbackProductDetails,
          cashback: {
            value: selectedCashbackProductCosts.value ?? 0,
            type: selectedCashbackProductCosts.type,
          },
        }),
      },

      contractSpan: getOfferContractSpanFromProducts(selectedProducts),

      contractStartDate: offer.contractStartDate ?? undefined,
      contractEndDate: selectedProducts?.find(product => product.contractEndDate)?.contractEndDate,

      usages: offer.usages,

      ...getOfferFutureRedeliveryFields(selectedProducts),
    };
  }
};

//Shortest-billing-period contracts first, longest last.
const BY_PERIOD_ORDER = ['H', 'D', 'W', 'M', 'Y'];

export const getOffers = (flowContext: FlowContextSme, offer?: Products_Offers_V3_OfferResponseModel): Offer[] => {
  const offerTypes: string[] = [];

  offer?.productCombinations?.forEach(combination => {
    // exclude the rental-device combination and any duplicate discountType (each duration can expose
    // a with-cashback and a without-cashback combination — they collapse into a single offer)
    const isRentalDeviceCombination = combination.products?.some(product => product.productType === 'huurapparaat');

    if (combination.discountType && !isRentalDeviceCombination && !offerTypes.includes(combination.discountType)) {
      offerTypes.push(combination.discountType);
    }
  });

  return offerTypes
    .map(offerType => getOffer({ ...flowContext, offerType }, offer))
    .filter((mappedOffer): mappedOffer is Offer => !!mappedOffer)
    .sort((a, b) => {
      const spanA = a.contractSpan?.[0];
      const spanB = b.contractSpan?.[0];

      const periodDiff =
        BY_PERIOD_ORDER.indexOf(spanA?.byPeriod ?? 'Y') - BY_PERIOD_ORDER.indexOf(spanB?.byPeriod ?? 'Y');

      return periodDiff !== 0 ? periodDiff : (spanA?.duration ?? 0) - (spanB?.duration ?? 0);
    });
};

export const getOfferTermsAndConditions = (
  flowContext: FlowContextSme,
  offer?: Products_Offers_V3_OfferResponseModel,
): OfferTermsAndConditions[] | undefined => {
  const { offerType, energyType, hasUpsellGas = false, hasUpsellElectricity = false } = flowContext;

  if (offer?.productCombinations && offerType && energyType) {
    const salesConditions: OfferTermsAndConditions[] = [];

    const selectedProductTypes = [...getProductTypesFromEnergyType(energyType), 'cashback' as const];
    const selectedProductCombination = selectOfferProductCombination(
      offer.productCombinations,
      selectedProductTypes,
      offerType,
      hasUpsellGas,
      hasUpsellElectricity,
    );

    selectedProductCombination?.products?.forEach(combinationProduct =>
      findOfferProduct(offer, combinationProduct.marketingProductCode)?.labels?.forEach(({ key, value, url }) => {
        if (key?.includes('generaltermsandconditions-ismkb.') && value && url) {
          salesConditions.push({ title: `${value}`, href: `${url}` });
        }
      }),
    );

    selectedProductCombination?.labels
      ?.filter(({ key }) => key?.includes('termsandconditions.'))
      .forEach(({ value, url }) => salesConditions.push({ title: `${value}`, href: `${url}` }));

    return salesConditions;
  }
};

const findMonthlyCosts = (item: { type: string }) => item.type === 'monthlyCosts';
export type PriceChangeStatus = 'increased' | 'decreased' | 'unchanged';

export function getMonthlyPriceChange(oldPrices: CostDetails[], newPrices: CostDetails[]): PriceChangeStatus {
  for (let i = 0; i < oldPrices.length; i++) {
    const oldMonthlyCosts = oldPrices[i].find(findMonthlyCosts);
    const newMonthlyCosts = newPrices[i].find(findMonthlyCosts);

    if (oldMonthlyCosts.costs.vatExcluded > newMonthlyCosts.costs.vatExcluded) {
      return 'decreased';
    }
    if (oldMonthlyCosts.costs.vatExcluded < newMonthlyCosts.costs.vatExcluded) {
      return 'increased';
    }
  }

  return 'unchanged';
}
